import { ProjectCode } from '@constants'
import { DELIVERY_CLASS } from '@hooks-api/types/dataModel/shipping'
import { type SubshipInfo } from '@hooks-api/types/dataModel/subship'
import { type Nullable } from '@hooks-api/types/nullable'
import { parseToNumber } from '@utils/parseToNumber'
import { sumOfArray } from '@utils/sumOfArray'
import { z } from 'zod'
import { type SpecificAddonCartItem, type CartItem, type GiveawayValue, type GiveawayValuesWithValidateStockItem, type RegularGiveawayValue } from '../types'

export const CART_FORM_ERROR_MESSAGE = {
  NOT_ENOUGH_STOCK: '庫存不足！',
  NOT_ENOUGH_GIVEAWAY: '贈品已贈完！',
  NOT_ENOUGH_GIVEAWAY_PARTIALLY: '部分贈品已贈完！',
  NOT_ENOUGH_GIVEAWAY_SELECTED: '請選擇贈品！',
  ADJUST_TO_MAX_QUANTITY: '調整為購買上限',
  NOT_ENOUGH_STOCK_FOR_ADDON_V2: '請調整商品數量為 0 的品項',
  EXCEED_PARENT_PRODUCT_PURCHASE_LIMIT: '超過母商品購買上限',
  ADDON_V2_PRICE_ADJUSTED: '加購品金額已調整',
} as const

export interface CartFormValues {
  cartItems: {
    cartItemId: CartItem['cartItemId']
    stock: CartItem['stock']
    quantity: CartItem['quantity']
    delivery_class: CartItem['delivery_class']
    is_addon_v2?: CartItem['is_addon_v2']
    addon_setting_id?: CartItem['addon_setting_id']
    subship_info?: {
      quantity_per_time: SubshipInfo['quantity_per_time']
    }
    addon_purchase_limit?: CartItem['addon_purchase_limit']
    parent_product_id?: CartItem['parent_product_id']
    parent_product_purchased_limit?: CartItem['parent_product_purchased_limit']
  }[]
  specificAddonItems: {
    cartItemId: SpecificAddonCartItem['cartItemId']
    stock: SpecificAddonCartItem['stock']
    quantity: SpecificAddonCartItem['quantity']
    delivery_class: SpecificAddonCartItem['delivery_class']
    addon_setting_id?: SpecificAddonCartItem['addon_setting_id']
    addon_purchase_limit?: SpecificAddonCartItem['addon_purchase_limit']
    addon_main_product_id: SpecificAddonCartItem['addon_main_product_id']
  }[]
  regularGiveaways?: RegularGiveawayValue[]
  giveaways?: GiveawayValue[]
}

/**
 * 判斷贈品是否全部都不能提供
 */
export const isAllGiveawayValuesUnavailable = (giveawayValues: GiveawayValuesWithValidateStockItem[]) =>
  giveawayValues.length > 0
  && giveawayValues.every(giveawayValue => giveawayValue.stock === 0)

/**
 * 判斷贈品選項是否部分不能提供
 */
export const isGiveawayValuePartiallyUnavailable = (giveawayValue: GiveawayValuesWithValidateStockItem) =>
  giveawayValue.stock !== undefined
  && giveawayValue.quantity > 0
  && (giveawayValue.stock === 0 || giveawayValue.stock < giveawayValue.quantity)

export const cartItemsSchema = z.array(z.object({
  cartItemId: z.string().or(z.number()),
  stock: z.number(),
  delivery_class: z.nativeEnum(DELIVERY_CLASS),
  subship_info: z.object({
    quantity_per_time: z.number(),
  }).optional(),
  quantity: z.number(),
  is_addon_v2: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  parent_product_id: z.number().optional(),
  parent_product_purchased_limit: z.number().optional().nullable(),
}))

export const specificAddonItemsSchema = z.array(z.object({
  cartItemId: z.string().or(z.number()),
  stock: z.number(),
  delivery_class: z.nativeEnum(DELIVERY_CLASS),
  quantity: z.number(),
  addon_setting_id: z.number().optional().nullable(),
  addon_purchase_limit: z.number().optional().nullable(),
  addon_main_product_id: z.number(),
  isDeleted: z.boolean().optional(),
}))

export const regularGiveawaysSchema: z.ZodType<RegularGiveawayValue[]> = z.array(z.object({
  couponCriterionId: z.number().optional(),
  isAllUnavailable: z.boolean(),
  isApplied: z.boolean(),
  selectedRegularGiveawayStock: z.number().optional(),
  product_id: z.string().optional(),
  sku: z.string().optional(),
  project_code: z.nativeEnum(ProjectCode).optional(),
  quantity: z.number().optional(),
}))

export const giveawaysSchema: z.ZodType<GiveawayValue[]> = z.array(z.object({
  couponCriterionId: z.number().optional(),
  totalSelected: z.number(),
  accumulatedGiftSelectionCount: z.number(),
  criterionCount: z.number().optional(),
  isGiveAll: z.boolean(),
  giveawayValuesWithValidateStock: z.array(z.object({
    product_id: z.string(),
    sku: z.string(),
    quantity: z.number(),
    stock: z.number().optional(),
    project_code: z.nativeEnum(ProjectCode),
  })),
}))

export const superRefineAddonCartItems = (cartItems: z.infer<typeof cartItemsSchema>, ctx: z.RefinementCtx) => {
  const addonV2Groups = new Map<
    string,
    { item: CartItem, index: number }[]
  >()

  cartItems.forEach((item, index) => {
    if (!item.is_addon_v2 || item.isDeleted) return
    if (!item.parent_product_id) return

    const key = item.parent_product_id.toString()
    const group = addonV2Groups.get(key) ?? []

    group.push({ item: item as CartItem, index })
    addonV2Groups.set(key, group)
  })

  addonV2Groups.forEach((group) => {
    const parentProductPurchasedLimit
      = group[0]?.item.parent_product_purchased_limit ?? 0

    const totalQuantity = group.reduce(
      (sum, { item }) => sum + item.quantity,
      0,
    )

    if (totalQuantity > parentProductPurchasedLimit && parentProductPurchasedLimit !== 0) {
      group.forEach(({ index }) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            CART_FORM_ERROR_MESSAGE.EXCEED_PARENT_PRODUCT_PURCHASE_LIMIT,
          path: ['cartItems', index],
        })
      })
    }
  })
}

export const superRefineCartItems = (cartItems: z.infer<typeof cartItemsSchema>, ctx: z.RefinementCtx) => {
  cartItems.forEach(({ stock, delivery_class, subship_info, quantity, is_addon_v2, isDeleted }, index) => {
    if (isDeleted) {
      return
    }
    if (delivery_class === DELIVERY_CLASS.NORMAL) {
      if (stock <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_STOCK,
          path: ['cartItems', index],
        })
      }
      if (stock < quantity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CART_FORM_ERROR_MESSAGE.ADJUST_TO_MAX_QUANTITY,
          path: ['cartItems', index],
        })
      }
      if (quantity === 0 && !is_addon_v2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_STOCK,
          path: ['cartItems', index],
        })
      }
      if (quantity === 0 && is_addon_v2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_STOCK_FOR_ADDON_V2,
          path: ['cartItems', index],
        })
      }
    }
    if (delivery_class === DELIVERY_CLASS.SUBSHIP) {
      // 寄倉商品 stock 小於第一次寄出數量，當作無庫存
      const quantityPerTime = parseToNumber(subship_info?.quantity_per_time)
      if (stock < quantityPerTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_STOCK,
          path: ['cartItems', index],
        })
      }
    }
  })
}

export const superRefineRegularGiveaways = (regularGiveaways: RegularGiveawayValue[] | undefined, ctx: z.RefinementCtx) => {
  regularGiveaways?.forEach((giveaway, index) => {
    const hasSelectedRegularGiveaway = !!giveaway.couponCriterionId && !!giveaway.product_id && !!giveaway.project_code && !!giveaway.sku
    /**
     * 常態滿額禮部分不足
     * ex: 所選的常態滿額禮結帳前發現庫存不足，但還有其他可選的
     */
    if (!giveaway.isAllUnavailable && giveaway.isApplied && hasSelectedRegularGiveaway && giveaway.selectedRegularGiveawayStock === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_PARTIALLY,
        path: ['regularGiveaways', index],
      })
    }
    /**
     * 常態滿額禮全部不足
     * ex: 所選的常態滿額禮結帳前發現全部庫存都不足
     */
    if (giveaway.isAllUnavailable && giveaway.isApplied && hasSelectedRegularGiveaway) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY,
        path: ['regularGiveaways', index],
      })
    }
    /**
     * 未選擇常態滿額禮
     */
    if (!giveaway.isAllUnavailable && giveaway.isApplied && !hasSelectedRegularGiveaway) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_SELECTED,
        path: ['regularGiveaways', index],
      })
    }
  })
}

const getHasSelectAll = ({
  giveawayValuesWithValidateStock,
  isGiveAll,
  isPartiallyNotEnough,
  totalStock,
  totalSelected,
  accumulatedGiftSelectionCount,
  criterionCount,
}: {
  giveawayValuesWithValidateStock: GiveawayValuesWithValidateStockItem[]
  isGiveAll?: boolean
  isPartiallyNotEnough: boolean
  totalStock: number
  totalSelected: number
  accumulatedGiftSelectionCount: number
  criterionCount?: Nullable<number>
}) => {
  if (isGiveAll) {
    return giveawayValuesWithValidateStock.every((giveawayValue) => {
      if (criterionCount != null && giveawayValue.stock !== undefined) {
        return giveawayValue.quantity === Math.min(criterionCount, giveawayValue.stock)
      }
      return true
    })
  }
  return isPartiallyNotEnough
    ? totalSelected === totalStock
    : accumulatedGiftSelectionCount === totalSelected
}

export const superRefineSpecificAddonItems = (specificAddonItems: z.infer<typeof specificAddonItemsSchema>, cartItems: z.infer<typeof cartItemsSchema>, ctx: z.RefinementCtx) => {
  const cartItemsUsedQuantityMap = cartItems.reduce((acc, { cartItemId, quantity }) => {
    acc.set(cartItemId, (acc.get(cartItemId) ?? 0) + quantity)
    return acc
  }, new Map<CartItem['cartItemId'], number>())

  const mergedUsedQuantityMap = specificAddonItems.reduce((acc, { cartItemId, quantity }) => {
    acc.set(cartItemId, (acc.get(cartItemId) ?? 0) + quantity)
    return acc
  }, new Map<CartItem['cartItemId'], number>())

  specificAddonItems.forEach(({ stock, quantity, isDeleted, addon_main_product_id, cartItemId }, index) => {
    const totalUsedQuantity = (mergedUsedQuantityMap.get(cartItemId) ?? 0) + (cartItemsUsedQuantityMap.get(cartItemId) ?? 0)
    if (isDeleted) {
      return
    }
    if (stock <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_STOCK,
        path: ['specificAddonItems', index],
      })
    }
    if (stock < totalUsedQuantity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.ADJUST_TO_MAX_QUANTITY,
        params: {
          addon_main_product_id,
          cartItemId,
        },
        path: ['specificAddonItems', index],
      })
    }
    if (quantity === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_STOCK,
        path: ['specificAddonItems', index],
      })
    }
  })
}

export const superRefineGiveaways = (giveaways: GiveawayValue[] | undefined, ctx: z.RefinementCtx) => {
  giveaways?.forEach((giveaway, index) => {
    const needSelectGiveaway = giveaway.accumulatedGiftSelectionCount > 0
    const totalStock = sumOfArray(giveaway.giveawayValuesWithValidateStock, (acc, giveawayValue) => acc + (giveawayValue.stock || 0))
    const isPartiallyNotEnough = totalStock < giveaway.accumulatedGiftSelectionCount
    const isAllUnavailable = isAllGiveawayValuesUnavailable(giveaway.giveawayValuesWithValidateStock)
    const hasSelectGiveaway = !!giveaway.couponCriterionId && giveaway.totalSelected > 0
    const hasSelectAll = getHasSelectAll({
      giveawayValuesWithValidateStock: giveaway.giveawayValuesWithValidateStock,
      isGiveAll: giveaway.isGiveAll,
      isPartiallyNotEnough,
      totalStock,
      totalSelected: giveaway.totalSelected,
      accumulatedGiftSelectionCount: giveaway.accumulatedGiftSelectionCount,
      criterionCount: giveaway.criterionCount,
    })

    /**
     * 贈品部分不足
     * ex: 所選的贈品結帳前發現庫存不足，但還有其他可選的
     */
    if (needSelectGiveaway && !isAllUnavailable) {
      giveaway.giveawayValuesWithValidateStock.forEach((giveawayValue) => {
        if (isGiveawayValuePartiallyUnavailable(giveawayValue)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_PARTIALLY,
            path: ['giveaways', index],
          })
        }
      })
    }

    /**
     * 贈品全部不足
     * ex: 所選的贈品結帳前發現全部庫存都不足
     */
    if (isAllUnavailable && hasSelectGiveaway) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY,
        path: ['giveaways', index],
      })
    }

    /**
     * 未選擇贈品
     */
    if (needSelectGiveaway && !isAllUnavailable && (!hasSelectGiveaway || !hasSelectAll)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_SELECTED,
        path: ['giveaways', index],
      })
    }
  })
}

export const cartFormSchema: z.ZodType<CartFormValues> = z
  .object({
    cartItems: cartItemsSchema,
    specificAddonItems: specificAddonItemsSchema,
    regularGiveaways: regularGiveawaysSchema.optional(),
    giveaways: giveawaysSchema.optional(),
  })
  .superRefine((data, ctx) => superRefineCartItems(data.cartItems, ctx))
  .superRefine((data, ctx) => superRefineAddonCartItems(data.cartItems, ctx))
  .superRefine((data, ctx) => superRefineSpecificAddonItems(data.specificAddonItems, data.cartItems, ctx))
  .superRefine((data, ctx) => superRefineRegularGiveaways(data.regularGiveaways, ctx))
  .superRefine((data, ctx) => superRefineGiveaways(data.giveaways, ctx))

/** 業務專站 cart validation */
export const cartFormB2bSchema: z.ZodType<Omit<CartFormValues, 'specificAddonItems'>> = z
  .object({
    cartItems: cartItemsSchema,
    regularGiveaways: regularGiveawaysSchema.optional(),
    giveaways: giveawaysSchema.optional(),
  })
  .superRefine((data, ctx) => superRefineCartItems(data.cartItems, ctx))
  .superRefine((data, ctx) => superRefineAddonCartItems(data.cartItems, ctx))
  .superRefine((data, ctx) => superRefineRegularGiveaways(data.regularGiveaways, ctx))
  .superRefine((data, ctx) => superRefineGiveaways(data.giveaways, ctx))

export const cartItemsSchemaForPromotion: z.ZodType<{ cartItems: CartFormValues['cartItems'] }> = z
  .object({
    cartItems: cartItemsSchema,
  })
  .superRefine((data, ctx) => superRefineCartItems(data.cartItems, ctx))
